import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild, OnInit, ViewEncapsulation } from '@angular/core';
import { DropdownItem } from '../models/dropdown-item.interface';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { DynamicQueryDecoratorService } from 'common/dynamicquery/dynamic-query-decorator.service';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { queueViewTabConfig } from './models/tab-config';
import { CoolLocalStorage } from '@angular-cool/storage';
import { bcSessionKeys } from 'common/common/constants/session-keys';
import { QueueViewColumn } from './models/column.interface';
import { GridItem } from '../models/grid-item.interface';
import { QueueViewTabService } from './queue-view-tab.service';
import { GridSetting } from './models/grid-setting.interface';
import { ColumnWidth } from './models/column-configs';
import { GridItemValueTransformerPipe } from './pipes/grid-item-value-transformer.pipe';
import { DynamicQueryAdapterService } from 'common/dynamicquery/dynamic-query-adapter.service';
import { BcRestApi } from 'common/api/rest-api';
import Moment from 'moment';
import { isEqual, sortBy, debounce } from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { WorkItemAdapter } from 'common/workitems/workitem-adapter';
import { BcConfigService } from 'common/config/provider';
import { QueueViewColumnSettings } from './models/column-settings.interface';
import { queueViewEvents, queueViewTabNames } from 'common/common/constants/analytics-enums';
import { DatePickerComponent } from 'shared/components/date-picker/date-picker.component';
import { CalendarDate } from 'shared/components/date-picker/calendar-date.interface';
import { UserContextService } from 'common/user-context/user-context.service';
import { GridItemListFilterPipe } from 'components/queue-view/queue-view-tab/pipes/grid-item-list-filter.pipe';
import { BackendFieldConverterService } from 'shared/services/backend-field-converter/backend-field-converter.service';
import { QueueViewPermissionsService } from 'components/queue-view/queue-view-tab/queue-view-permissions.service';
import { QueueViewCheckBoxHelper } from 'components/queue-view/queue-view-checkbox-helper';
import { from, Subscription } from 'rxjs';
import { LogService } from 'common/logger/log.service';
import { LoggerService } from 'common/logger/logger.service';
import { WindowSizeService } from 'common/window-size/window-size.service';
import { WindowSize } from 'common/window-size/window-size';
import { WorkItemExportService } from 'components/queue-view/queue-view-tab/work-item-export/work-item-export.service';
import { CommonAuthService } from 'common/auth/common-auth.service';

@Component({
  selector: 'app-queue-view-tab',
  templateUrl: './queue-view-tab.component.html',
  styleUrls: ['./queue-view-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class QueueViewTabComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  tabId: string;

  @Input()
  selectedDepartments;

  @ViewChild('datePicker') datePicker: DatePickerComponent;

  public gridSetting: GridSetting;
  public folderList: DropdownItem[];
  public selectedFolderId: string[] | string;
  public isListLoading: boolean = false;
  public workItemType: string;
  public columnPickerVisible: boolean = false;
  public gridItemList: GridItem[] = [];
  public anyChecked: boolean = false;
  public allChecked: boolean = false;
  public isFlagFilterActive: boolean = false;
  public isOwnFilterActive: boolean = false;
  public isWarningFilterActive: boolean = false;
  public transferPanelOpened: boolean = false;
  public filterGrid: string = '';
  public selectedPreviewItem;
  public selectedPreviewItemType: WorkItemTypes;
  public refreshTimestamp;
  public selectedColumn: QueueViewColumn;
  public workItemTypeList;
  public defaultColumnsWithTimeConditions;
  public tabName: string = '';
  public checkedItemType: WorkItemTypes;
  public windowSize: WindowSize;
  public readonly isHybrid = this._commonAuth.isHybrid();

  private _workItemDropdownValues = [
    { value: WorkItemTypes.CHAT, label: 'Chat' },
    { value: WorkItemTypes.EMAIL, label: 'Email' }
  ];
  private _tabs = queueViewTabConfig;
  private _insertSubscription: Subscription;
  private _updateSubscription: Subscription;
  private _deleteSubscription: Subscription;
  private _doneSubscription: Subscription;
  private _foldersSubscription: Subscription;
  private _isFirstQueryDone: boolean;
  private _columnWidthSum: string;
  private _rootFolder: DropdownItem;
  private _startDate: number;
  private _endDate: number;
  private _conditionTimeouts = {};
  private _allFolderIDs = [];
  private _logger: LoggerService;
  private _windowSizeSubscription: Subscription;

  private readonly DETECT_CHANGES_DEBOUNCE_TIME = 300; // ms
  private readonly DETECT_CHANGES_DEBOUNCE_MAX_WAIT = 500; // ms

  constructor(private _queueViewTabService: QueueViewTabService,
              private _dynamicQueryDecorator: DynamicQueryDecoratorService,
              private _bcAnalytics: BcAnalytics,
              private _dynamicQuery: DynamicQueryAdapterService,
              private localStorageService: CoolLocalStorage,
              private _changeDetector: ChangeDetectorRef,
              private _gridItemValueTransformerPipe: GridItemValueTransformerPipe,
              private _bcRestApi: BcRestApi,
              private _translateService: TranslateService,
              private _bcConfig: BcConfigService,
              public _userContextService: UserContextService,
              private _gridItemListFilter: GridItemListFilterPipe,
              private _backendFieldConverter: BackendFieldConverterService,
              private _queueViewPermissionsService: QueueViewPermissionsService,
              private _logService: LogService,
              private _windowSizeService: WindowSizeService,
              private _workItemExportService: WorkItemExportService,
              private _commonAuth: CommonAuthService) {
    this._loadChannelSetting();
    this._logger = this._logService.getLoggerInstance(QueueViewTabComponent.name);
  }

  private _loadChannelSetting(): void {
    if (this._bcConfig.current.features.messagingMonitorView) {
      this._workItemDropdownValues.push({ value: WorkItemTypes.SMS, label: 'Messaging' });
    }

    const savedChannelType = this.localStorageService.getItem(bcSessionKeys.storage.queueViewSelectedChannelType);
    if (savedChannelType) {
      this.workItemType = savedChannelType;
    }
  }

  private _setChannelType(newWorkItemType: string): void {
    this.workItemType = newWorkItemType;
    this.localStorageService.setItem(bcSessionKeys.storage.queueViewSelectedChannelType, newWorkItemType);
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes.tabId) {
      this.isListLoading = false;
      this._updateChannelsByTabId();
      this.tabName = queueViewTabNames[this.tabId];
    }
    await this._loadTab();
  }

  ngOnInit(): void {
    this._windowSizeSubscription = this._windowSizeService.onResize$.subscribe(change => {
      this.windowSize = change.current;
      this._changeDetector.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this._clearSubscriptions();
    this._clearAllConditionTimeouts();
    this._clearFoldersSubscriptions();
    this._clearWindowSizeSubscription();
  }

  private _clearAllConditionTimeouts(): void {
    if (!this._conditionTimeouts) {
      return;
    }
    for (let row of Object.keys(this._conditionTimeouts)) {
      if (!row) {
        continue;
      }
      for (let column of Object.keys(this._conditionTimeouts[row])) {
        if (!column) {
          continue;
        }
        if (this._conditionTimeouts[row][column]) {
          clearTimeout(this._conditionTimeouts[row][column]);
        }
      }
    }
    this._conditionTimeouts = {};
  }

  private _detectChanges(): void {
    this._debouncedDetectChanges();
  }

  private _debouncedDetectChanges = debounce(() => {
    if (!this._changeDetector['destroyed']) { //eslint-disable-line @typescript-eslint/dot-notation
      this._changeDetector.detectChanges();
    }
  }, this.DETECT_CHANGES_DEBOUNCE_TIME, {
    leading: true, trailing: true,
    maxWait: this.DETECT_CHANGES_DEBOUNCE_MAX_WAIT
  });

  calculateColumnWidthSum() {
    this._columnWidthSum = this.gridSetting.columnConfig.reduce((sum, column) => {
      if (this.isColumnHidden(column)) {
        return sum;
      } else {
        return sum + parseInt(column.baseWidth, 10);
      }
    }, 0) + 'px';
  }

  public filteredGridItems = this.gridItemList;

  filtersChanged(): void {
    this.filteredGridItems = this._gridItemListFilter.transform(this.gridItemList,
      this.filterGrid, this.gridSetting, this.isFlagFilterActive, this.isWarningFilterActive);
  }

  get hasFinishedLoading(): boolean {
    return !!this.folderList && !this.isListLoading;
  }

  get columnWidthSum(): string {
    return this._columnWidthSum;
  }

  get columnWidthSumWrapper(): string {
    // Adjusting for scrollbar width
    return (parseInt(this._columnWidthSum, 10) + 6) + 'px';
  }

  public get availabilityWorkItemType(): WorkItemTypes {
    return this.isMessagingSelected ? this.checkedItemType : this.workItemType;
  }

  isSelectionHidden(row): boolean {
    return this.isMessagingSelected && this.checkedItemType && this._backendFieldConverter.chatTypeConverter(row.ChatType) !== this.checkedItemType;
  }

  getValue(row, column) {
    return this._gridItemValueTransformerPipe.transform(row, column);
  }

  get hasAssignPermission(): boolean {
    return this._queueViewPermissionsService.hasAssignPermission(this.workItemType);
  }

  get hasClosePermission(): boolean {
    return this._queueViewPermissionsService.hasClosePermission(this.workItemType);
  }

  get hasColumnChooser(): boolean {
    return this._queueViewPermissionsService.hasColumnChooserPermission(this.workItemType);
  }

  public get isEmailClosedTabActive(): boolean {
    return this.tabId === queueViewTabConfig.CLOSED.id && this.workItemType === WorkItemTypes.EMAIL;
  }

  public get isChatBotTabActive(): boolean {
    return this.tabId === queueViewTabConfig.CHATBOT.id;
  }

  public get isAssignedTabActive(): boolean {
    return this.tabId === queueViewTabConfig.ASSIGNED.id;
  }

  public get isClosedTabActive(): boolean {
    return this.tabId === queueViewTabConfig.CLOSED.id;
  }

  public get isMessagingSelected(): boolean {
    return this.workItemType === WorkItemTypes.SMS;
  }

  public get shouldDisplayFlagFilter(): boolean {
    return !this.isChatBotTabActive && this.workItemType === WorkItemTypes.CHAT;
  }

  public get shouldEscalateChat(): boolean {
    return this.isChatBotTabActive && this.workItemType === WorkItemTypes.CHAT;
  }

  public get shouldDisplayOwnFilter(): boolean {
    return (this.isAssignedTabActive &&
      ((this.workItemType === WorkItemTypes.CHAT && this._queueViewPermissionsService.hasGridViewActivePermission(WorkItemTypes.CHAT)
        || this.workItemType === WorkItemTypes.EMAIL
        || (this.workItemType === WorkItemTypes.SMS && this._queueViewPermissionsService.hasGridViewActivePermission(WorkItemTypes.SMS))))
      ||
      (this.isClosedTabActive &&
        ((this.workItemType === WorkItemTypes.CHAT && this._queueViewPermissionsService.hasGridViewInactivePermission(WorkItemTypes.CHAT))
          || this.workItemType === WorkItemTypes.EMAIL
          || (this.workItemType === WorkItemTypes.SMS && this._queueViewPermissionsService.hasGridViewInactivePermission(WorkItemTypes.SMS)))));
  }

  public get shouldDisplayFilterText(): boolean {
    return this.windowSize === WindowSize.LARGE;
  }

  openPreview(item: GridItem) {
    if (this.selectedPreviewItem === item) {
      this.closePreview();
      return;
    }

    if (this.workItemType === WorkItemTypes.CHAT ||
      (this._bcConfig.current.features.emailPreview && this.workItemType === WorkItemTypes.EMAIL) || this.workItemType === WorkItemTypes.SMS) {
      this.selectedPreviewItem = item;
      this.selectedPreviewItemType = this.workItemType === WorkItemTypes.SMS ? this._backendFieldConverter.chatTypeConverter((<any>item).ChatType) : this.workItemType;
      this._bcAnalytics.eventTrack(queueViewEvents.ITEM_SELECTED, {
        at: queueViewTabNames[this.tabId],
        itemType: this.selectedPreviewItemType
      });
    }
  }

  closePreview() {
    this.selectedPreviewItem = null;
  }

  onModelChange(): void {
    this.gridItemList.forEach((item) => {
      if (this.filteredGridItems.indexOf(item) === -1) {
        item.checked = false;
      }
    });
    this._listItemsUpdated();
  }

  onAssignToClicked(): void {
    this.transferPanelOpened = true;
  }

  closeTransferPanel(): void {
    this.transferPanelOpened = false;
  }

  successfulTransfer(): void {
    this.closeTransferPanel();
    this.toggleAllSelection(false);
    this._updateCheckedState();
  }

  toggleAllSelection(shouldBeSelected): void {
    QueueViewCheckBoxHelper.toggleAllSelection(this.filteredGridItems, shouldBeSelected);
  }

  getLabelByWorkItemType(labelStr: string) {
    switch (this.workItemType) {
      case WorkItemTypes.CHAT:
        return `queueViewTab.labels.chat.${labelStr}`;
      case WorkItemTypes.EMAIL:
        return `queueViewTab.labels.email.${labelStr}`;
      case WorkItemTypes.SMS:
        return `queueViewTab.labels.messaging.${labelStr}`;
      default:
        throw new Error('Unhandled work item type at label groups');
    }
  }

  checkAllOrNone(): void {
    this.allChecked = !this._areAllRowsChecked();
    this.toggleAllSelection(this.allChecked);
    this.anyChecked = this._isAnyRowChecked();
  }

  async onCloseClicked(): Promise<void> {
    const itemIds = this.getSelectedItemIds();
    this._queueViewTabService.endItems(this.workItemType, itemIds);
    this.toggleAllSelection(false);
    this._updateCheckedState();
  }

  async onAssignToMeClicked(): Promise<void> {
    const itemIds = this.getSelectedItemIds();
    this._queueViewTabService.assignItems(this.workItemType, itemIds, this.shouldEscalateChat);
    this.toggleAllSelection(false);
    this._updateCheckedState();
  }

  public async onReopenThreadsClicked(): Promise<void> {
    if (this.getSelectedItemIds().length !== 0) {
      this._queueViewTabService.reopenEmailThreads(this.getSelectedItemIds())
        .then(() => {
          this.reloadClosedWorkItems();
        });
    }
  }

  public getSelectedItemIds(): string[] {
    return this.filteredGridItems
      .filter(queueElem => queueElem.checked)
      .map(elem => elem.ItemID);
  }

  isColumnHidden(column: QueueViewColumn): boolean {
    let selectedColumn = this.gridSetting.columnSettings.find(col => col.id === column.id);
    if (!selectedColumn) {
      return false;
    }
    return this.hasColumnChooser ? (column && column.id && selectedColumn.isHidden) : false;
  }

  showColumnPicker(): void {
    this.columnPickerVisible = true;
  }

  changeChecked(row): void {
    row.checked = !row.checked;

    if (this.workItemType === WorkItemTypes.SMS) {
      this.checkedItemType = this._backendFieldConverter.chatTypeConverter(row.ChatType);
    }

    this._updateCheckedState();

    if (row.checked) {
      this._bcAnalytics.eventTrack(queueViewEvents.ITEM_SELECTED, {
        at: queueViewTabNames[this.tabId],
        itemType: this.workItemType
      });
    }
  }

  columnSettingsUpdated(newColumnSettings): void {
    if (newColumnSettings) {
      this.gridSetting.columnSettings = newColumnSettings;
      const convertedValues = JSON.stringify(this.gridSetting.columnSettings);
      this.localStorageService.setItem(bcSessionKeys.storage.queueViewColumnSettings + this.gridSetting.name, convertedValues);
      this.calculateColumnWidthSum();
      this.updateWarningForAllItems();

      this._bcAnalytics.eventTrack(queueViewEvents.COLUMNS_CHANGED, {
        at: queueViewTabNames[this.tabId]
      });
    }
    this.columnPickerVisible = false;
  }

  initSubscriptions(folderIds: string[]): void {
    let departmentIDs: string[] = this._getSelectedDepartmentIDs();

    if (this.isChatBotTabActive) {
      folderIds = undefined;
      departmentIDs = undefined;
    }

    this.isListLoading = true;
    this._changeDetector.detectChanges();
    this._resetItemList();
    this._clearSubscriptions();
    this._clearAllConditionTimeouts();
    const queryParams: any = { FolderIDs: folderIds, DepartmentIDs: departmentIDs };
    if (this.isAssignedTabActive && this.isOwnFilterActive) {
      queryParams.ActualOperatorID = this._userContextService.userContext.id;
    }
    const activeWorkItemsObservable = this._dynamicQuery.getQuery(this.gridSetting.dynamicQueryName, queryParams);
    this._dynamicQueryDecorator.create(activeWorkItemsObservable, item => item);
    this._insertSubscription = activeWorkItemsObservable.inserted()
      .subscribe(dynamicQueryEvent => this._itemInserted(dynamicQueryEvent.data));
    this._updateSubscription = activeWorkItemsObservable.updated()
      .subscribe(dynamicQueryEvent => this._itemUpdated(dynamicQueryEvent.data));
    this._deleteSubscription = activeWorkItemsObservable.deleted()
      .subscribe(dynamicQueryEvent => this._itemDeleted(dynamicQueryEvent.data));
    this._doneSubscription = activeWorkItemsObservable.done()
      .subscribe(() => this._queryDone());
  }

  folderSelected(selectedItem: DropdownItem): void {
    if (!isEqual(this.selectedFolderId, selectedItem.id)) {
      this.selectedFolderId = selectedItem.id;
      this._saveSelectedFoldersToStorage();
      this._resetItemList();
      this._clearAllConditionTimeouts();
      this._initGridItems(selectedItem.id);
    }
  }

  reloadClosedWorkItems() {
    this._storeDatePickerSettings();
    this._getInactiveWorkItems(this._formatFolders(this.selectedFolderId));
  }

  public isFilteredGridItemsEmpty(): boolean {
    return this.filteredGridItems.length === 0;
  }

  public onDateChanged(event) {
    this._startDate = event.start.valueOf();
    this._endDate = event.end.valueOf();
    this.reloadClosedWorkItems();
  }

  public isColumnSortingAscending(): boolean {
    return this.selectedColumn && this.selectedColumn.sortDirection === 'asc';
  }

  public sortByColumns(column) {
    if (this.selectedColumn.valueProperty === column.valueProperty) {
      this.selectedColumn.sortDirection = this.isColumnSortingAscending() ? 'desc' : 'asc';
    } else {
      this.selectedColumn = column;
    }
    this._setColumnSortingSettings(this.selectedColumn);
    this._sortGridItemList();
    this._listItemsUpdated();
  }

  public getColumnSortingSettings() {
    const savedSortingSetting = this.localStorageService.getItem(bcSessionKeys.storage.queueViewColumnSort + this._tabs[this.tabId][this.workItemType].name);
    if (!savedSortingSetting) {
      return null;
    }
    return JSON.parse(savedSortingSetting);
  }

  public onWorkItemTypeChange(selectedWorkItemType): Promise<void> {
    if (!selectedWorkItemType) {
      return;
    }
    this._resetFilters();
    this._setChannelType(selectedWorkItemType.value);
    this.folderList = null;
    this.selectedFolderId = null;
    return this._loadTab(true);
  }

  public isAnyWarningInColumn(column: QueueViewColumn): boolean {
    return this.gridItemList.some((row) => this.hasColumnWarning(row, column));
  }

  public isAnyWarningInRow(row: GridItem): boolean {
    if (!row.columnsShouldBeWarned || !row.columnsShouldBeWarned.length) {
      return false;
    }
    return row.columnsShouldBeWarned.some((column) => {
      let columnWithWarning = this.gridSetting.columnSettings.find(col => col.id === column);
      return columnWithWarning && !columnWithWarning.isHidden;
    });
  }

  public hasColumnWarning(row: GridItem, column: QueueViewColumn): boolean {
    if (row.columnsShouldBeWarned) {
      return this.hasColumnConditionSet(column) && row.columnsShouldBeWarned.includes(column.id);
    }
  }

  public getOwnItemsCount(): number {
    const ownItems = this.filteredGridItems.filter(item => item.OperatorID === this._userContextService.userContext.id);
    return ownItems.length;
  }

  public getWarnedRowsCount(): number {
    const rowsWithWarnings = this.filteredGridItems.filter((item) => item.columnsShouldBeWarned && item.columnsShouldBeWarned.length > 0);
    return rowsWithWarnings.length;
  }

  public getFlaggedRowsCount(): number {
    const flaggedRows = this.filteredGridItems.filter((item) => item.Flagged);
    return flaggedRows.length;
  }

  public areActionsAvailable(): boolean {
    return (this.gridSetting.hasActions && this._queueViewPermissionsService.isServiceTypeAvailable(this.workItemType)) && this._hasPermissions() && this._hasBotChatActions();
  }

  public onFilterFlagsClick(): void {
    this.isFlagFilterActive = !this.isFlagFilterActive;
    this.filtersChanged();
  }

  public onOwnFilterClick(): void {
    this.isOwnFilterActive = !this.isOwnFilterActive;
    this._loadTab();
  }

  public onWarningFilterClick(): void {
    this.isWarningFilterActive = !this.isWarningFilterActive;
    this.filtersChanged();
  }

  public async setSelectedFoldersToAll(): Promise<void> {
    const cachedFolders = await this._queueViewTabService.getFolders(this.workItemType);
    this._allFolderIDs = cachedFolders.map(folder => folder.id);
    this.selectedFolderId = this._allFolderIDs;
    this._saveSelectedFoldersToStorage();
  }

  public setFilterGrid(filterText: string) {
    this.filterGrid = filterText;
  }

  public getIconNameForFilter(filterPrefix: string, isActive: boolean): string {
    return filterPrefix + (isActive ? '-filled' : '-empty');
  }

  public exportWorkItems(): void {
    this._workItemExportService.exportWorkItems(this.filteredGridItems, this.isClosedTabActive, this.gridSetting.name);
  }

  private _resetFilters() {
    this.isFlagFilterActive = false;
    this.isOwnFilterActive = false;
    this.isWarningFilterActive = false;
    this.filterGrid = '';
    this.filtersChanged();
  }

  private _hasBotChatActions(): boolean {
    return this.isChatBotTabActive ? this._bcConfig.current.features.botChatActions : true;
  }

  private _hasPermissions(): boolean {
    return this.hasClosePermission || this.hasAssignPermission;
  }

  private _updateChannelsByTabId(): void {
    this.workItemTypeList = this._workItemDropdownValues.filter(dropdownOption => {
      return (this._tabs[this.tabId].availableWorkItemTypes.includes(dropdownOption.value));
    }).map(item => {
      return { ...item };
    });
    if (this.workItemTypeList.length &&
      (!this.workItemType || !this._tabs[this.tabId].availableWorkItemTypes.includes(this.workItemType))) {
      this._setChannelType(this.workItemTypeList[0].value);
    }
    const selectedOption = this.workItemTypeList.find(item => item.value === this.workItemType);
    if (selectedOption) {
      selectedOption.selected = true;
    }
    this._changeDetector.detectChanges();
  }

  private async _loadTab(changeFolder?: boolean): Promise<void> {
    this._clearFoldersSubscriptions();
    this.gridItemList = [];
    this.filtersChanged();
    this._initTab(this.tabId, this.workItemType);
    this._loadColumnSettings();

    if (!this.folderList || changeFolder) {
      this._changeDetector.detectChanges(); // Force reinit of folder dropdown
      this._foldersSubscription = from(this._initFolders()).subscribe(() => {
        this._setupGridData();
      });
    } else {
      this._setupGridData();
    }
  }

  private _setupGridData() {
    this._setDatesIfClosedTabIsActive();

    this._loadColumnSortSetting();

    this._initGridItems(this.selectedFolderId);
    this.selectedColumn = this.getColumnSortingSettings();
    this._listItemsUpdated();
  }

  private _loadColumnSortSetting() {
    if (!this.getColumnSortingSettings()) {
      if (this.isClosedTabActive) {
        this._setColumnSortingSettings(this.gridSetting.columnConfig.find(column => column.valueProperty === 'Closed'));
      }
      switch (this.workItemType) {
        case WorkItemTypes.EMAIL:
          this._setColumnSortingSettings(this.gridSetting.columnConfig.find(column => column.valueProperty === 'LastIncomingArrived'));
          break;
        case WorkItemTypes.CHAT:
          this._setColumnSortingSettings(this.gridSetting.columnConfig.find(column => column.valueProperty === 'Created'));
          break;
        case WorkItemTypes.SMS:
          this._setColumnSortingSettings(this.gridSetting.columnConfig.find(column => column.valueProperty === 'LastMessageSent'));
          break;
      }
    }
  }

  private _setDatesIfClosedTabIsActive() {
    if (this.isClosedTabActive) {
      this._setDatesByWorkItemType(this.workItemType);
      this.reloadClosedWorkItems();
    }
  }

  private _setColumnSortingSettings(column?: QueueViewColumn) {
    let columnToUse = column;
    if (!column) {
      columnToUse = this.selectedColumn;
    }
    this.localStorageService.setItem(bcSessionKeys.storage.queueViewColumnSort + this._tabs[this.tabId][this.workItemType].name, JSON.stringify(columnToUse));
  }

  private _sortGridItemList() {
    if (!this.selectedColumn) {
      return;
    }
    let sortedList = this.gridItemList.slice(0);
    let sorted;
    sorted = this.isColumnSortingAscending() ? sortBy(sortedList, [this.selectedColumn.valueProperty]) : sortBy(sortedList, [this.selectedColumn.valueProperty]).reverse();
    this.gridItemList = sorted;
  }

  private _resetItemList(): void {
    this.gridItemList.length = 0;
    this.filtersChanged();
  }

  private _itemInserted(item: GridItem): void {
    this.setWarningsForOneItem(item);
    const customFieldObject = this._getCustomFieldObject(item);
    item = { ...item, ...customFieldObject };
    this._updateColumnConfig(Object.keys(customFieldObject));

    this.gridItemList.push(item);
    this._listItemsUpdated();
  }

  private _getCustomFieldObject(data) {
    let newData = {};
    if (data.CustomFields) {
      const customFields: string[] = data.CustomFields.split(',');
      customFields.forEach(fields => {
        let customField: string[] = fields.split('=');
        customField = this._replaceEscapeChars(customField);
        newData[customField[0]] = customField[1];
      });
    }
    return newData;
  }

  private _replaceEscapeChars(customField: string[]): string[] {
    return customField.map(field => {
      return field.replace(/\\c/g, ',').replace(/\\e/g, '=');
    });
  }

  private _itemUpdated(item: GridItem): void {
    const indexInList = this.gridItemList.findIndex(listItem => listItem.ItemID === item.ItemID);
    const customFieldObject = this._getCustomFieldObject(item);
    item = { ...item, ...customFieldObject };

    if (indexInList === -1) {
      this.gridItemList.push(item);
      item.checked = false;
      this._updateColumnConfig(Object.keys(customFieldObject));
    } else {
      const wasRowSelected = this.gridItemList[indexInList].checked;
      this.gridItemList[indexInList] = item;
      this.gridItemList[indexInList].checked = wasRowSelected;
      this._updateColumnConfig(Object.keys(customFieldObject));
    }
    this.setWarningsForOneItem(item);
    this._listItemsUpdated();
  }

  private _updateColumnConfig(customColumnNames: string[]): void {
    const customColumnsToAdd: string[] = customColumnNames.filter(name => this._filterConfigByName(this.gridSetting.columnConfig, name).length === 0);
    customColumnsToAdd.forEach(columnName => {
      const newConfig: QueueViewColumn = {
        displayName: columnName,
        baseWidth: ColumnWidth.MEDIUM,
        valueProperty: columnName,
        id: columnName
      };
      this.gridSetting.columnConfig.push(newConfig);
      this.addToColumnSettings(newConfig);
    });
    if (customColumnsToAdd.length) {
      this.calculateColumnWidthSum();
    }
  }

  private addToColumnSettings(column): void {
    let newItem: QueueViewColumnSettings = { id: column.id, displayName: column.displayName, isHidden: false, condition: column.condition };
    let isColumnInSettings = this._tabs[this.tabId][this.workItemType].columnSettings.find(col => col.id === column.id);
    if (!isColumnInSettings) {
      this._tabs[this.tabId][this.workItemType].columnSettings.push(newItem);
    }
  }

  private setWarningsForOneItem(row: GridItem): void {
    const settings = this._tabs[this.tabId][this.workItemType].columnSettings;
    row.columnsShouldBeWarned = [];
    if (settings) {
      settings.forEach((condColumn) => {
        if (!condColumn.isHidden) {
          let gridSettingColumn = this.gridSetting.columnConfig.find(col => col.id === condColumn.id);
          if (gridSettingColumn && condColumn.condition && row[gridSettingColumn.valueProperty]) {
            const conditionInMillisec = (condColumn.condition.minutes * 60000) + (condColumn.condition.seconds * 1000);
            let valueProperty = row[gridSettingColumn.valueProperty];
            if (valueProperty) {
              let originalValue = this.createOriginalValueInMilliseconds(valueProperty);
              const warningStartTime = originalValue + conditionInMillisec;
              this.collectColumnsShouldBeWarned(row, condColumn, warningStartTime);
            }
          }
        }
      });
    }
  }

  private createOriginalValueInMilliseconds(valueProperty: string): number {
    if (this.hasOnlyNumbers(valueProperty)) {
      return parseInt(valueProperty, 10);
    }
    return Moment(valueProperty).valueOf();
  }

  private hasOnlyNumbers(string): boolean {
    return string.match(/^[0-9]*$/g);
  }

  private collectColumnsShouldBeWarned(row: GridItem, condColumn: QueueViewColumn, warningStartTime: number): void {
    if (warningStartTime <= Moment().valueOf()) {
      if (!row.columnsShouldBeWarned.includes(condColumn.id)) {
        row.columnsShouldBeWarned.push(condColumn.id);
      }
      return;
    } else {
      let timeout = warningStartTime - Moment().valueOf();
      if (!row.columnsShouldBeWarned.includes(condColumn.id)) {
        this.setTimeoutHelper(timeout, condColumn.id, row);
      }
    }
  }

  private setTimeoutHelper(timeout: number, columnId: string, row: GridItem): void {
    if (this._conditionTimeouts[row.ItemID] && this._conditionTimeouts[row.ItemID][columnId]) {
      clearTimeout(this._conditionTimeouts[row.ItemID][columnId]);
      this._conditionTimeouts[row.ItemID][columnId] = undefined;
    }
    if (!this._conditionTimeouts[row.ItemID]) {
      this._conditionTimeouts[row.ItemID] = {};
    }
    this._conditionTimeouts[row.ItemID][columnId] = setTimeout(() => {
      row.columnsShouldBeWarned.push(columnId);
      this._detectChanges();
    }, timeout);
  }

  private hasColumnConditionSet(column: QueueViewColumn): boolean {
    const settings = this._tabs[this.tabId][this.workItemType].columnSettings;
    if (settings) {
      let settingsColumn = settings.find((condColumn) => condColumn.id === column.id);
      if (settingsColumn) {
        return !!settingsColumn.condition;
      }
    }
  }

  private updateWarningForAllItems() {
    const settings = this._tabs[this.tabId][this.workItemType].columnSettings;
    if (settings) {
      settings.forEach(_condColumn => {
        this.gridItemList.forEach((item) => {
          this.setWarningsForOneItem(item);
        });
      });
    }
  }

  private _filterConfigByName(configToUpdate: QueueViewColumn[], name: string): QueueViewColumn[] {
    return configToUpdate.filter(config => {
      return config.displayName === name;
    });
  }

  private _itemDeleted(item: GridItem): void {
    const indexInList = this.gridItemList.findIndex(listItem => listItem.ItemID === item.ItemID);

    if (indexInList !== -1) {
      this.gridItemList.splice(indexInList, 1);
    }
    if (this._conditionTimeouts[item.ItemID]) {
      if (this._conditionTimeouts[item.ItemID].length) {
        this._conditionTimeouts[item.ItemID].forEach((columnCond) => {
          clearTimeout(columnCond);
        });
      }
      this._conditionTimeouts[item.ItemID] = undefined;
    }
    this._listItemsUpdated();
  }

  private _listItemsUpdated(): void {
    this.gridItemList = this.gridItemList.slice(0);
    this._sortGridItemList();
    this._detectChanges();
    this._updateCheckedState();
    this.filtersChanged();
  }

  private _updateCheckedState(): void {
    this.allChecked = this._areAllRowsChecked();
    this.anyChecked = this._isAnyRowChecked();

    if (!this.anyChecked && this.isMessagingSelected) {
      this.checkedItemType = null;
    }

    this._detectChanges();
  }

  private _areAllRowsChecked(): boolean {
    if (this.filteredGridItems) {
      return QueueViewCheckBoxHelper.areAllRowsChecked(this.filteredGridItems);
    }
    return false;
  }

  private _isAnyRowChecked(): boolean {
    if (this.filteredGridItems) {
      return QueueViewCheckBoxHelper.isAnyRowChecked(this.filteredGridItems);
    }
    return false;
  }

  private _queryDone(): void {
    this.isListLoading = false;
    if (!this._isFirstQueryDone) {
      this._isFirstQueryDone = true;
      this._bcAnalytics.eventTrack(queueViewEvents.ITEM_COUNT_AT_OPEN, {
        workItemType: this.workItemType,
        tabId: this.tabId,
        queueCount: this.gridItemList.length
      });
    }
    this._changeDetector.detectChanges();
  }

  private async _initFolders(): Promise<void> {
    await this._loadFolders();
    const savedFolderSetting = this.localStorageService.getItem(bcSessionKeys.storage.queueViewSelectedFolderId + this.gridSetting.name.toLowerCase());
    let convertedFolderSetting = null;
    if (savedFolderSetting) {
      convertedFolderSetting = JSON.parse(savedFolderSetting);
    }
    if (convertedFolderSetting && this._doesFolderExistInDropdown(convertedFolderSetting)) {
      // "All folders" needs the original array reference
      if (convertedFolderSetting instanceof Array) {
        const folderWithSavedIds = this.folderList.find(folder => isEqual(folder.id, convertedFolderSetting));
        if (folderWithSavedIds) {
          this.selectedFolderId = folderWithSavedIds.id;
        }
      } else {
        this.selectedFolderId = convertedFolderSetting;
      }
    } else {
      this._initWithDefaultFolder();
    }
    this._changeDetector.detectChanges();
  }

  private _saveSelectedFoldersToStorage(): void {
    this.localStorageService.setItem(bcSessionKeys.storage.queueViewSelectedFolderId + this.gridSetting.name.toLowerCase(), JSON.stringify(this.selectedFolderId));
  }

  private _doesFolderExistInDropdown(folderValue) {
    return this.folderList.find(folder => isEqual(folder.id, folderValue));
  }

  private async _loadFolders(): Promise<void> {
    const cachedFolders = await this._queueViewTabService.getFolders(this.workItemType);
    const loadedFolders = cachedFolders.slice(0);
    this._allFolderIDs = loadedFolders.map(folder => folder.id);
    const translatedAllFolderSelector = await this._translateService.get('queueViewTab.allFolderSelector').toPromise();
    const allFolders: DropdownItem = { id: this._allFolderIDs, name: translatedAllFolderSelector, parentId: null };
    this._rootFolder = loadedFolders.find(folder => folder.parentId === null && folder.name !== allFolders.name);
    this.folderList = loadedFolders.sort((a, b) => a.name.localeCompare(b.name));
    this.folderList.unshift(allFolders);
  }

  private _formatFolders(folderId) {
    return folderId instanceof Array ? folderId : [folderId];
  }

  private _initWithDefaultFolder(): void {
    if (this.folderList && this._rootFolder) {
      this.selectedFolderId = this._rootFolder.id;
    } else if (this.folderList && this.folderList.length > 0 && !this._rootFolder) {
      this.selectedFolderId = this.folderList[0].id;
    }
  }

  private _loadColumnSettings(): void {
    const savedSetting: string = this.localStorageService.getItem(bcSessionKeys.storage.queueViewColumnSettings + this.gridSetting.name);
    if (!savedSetting) {
      let columns = [];
      this._tabs[this.tabId][this.workItemType].columnConfig.forEach((column) => {
        if (this._tabs[this.tabId][this.workItemType].defaultHiddenColumns.has(column)) {
          columns.push({ id: column.id, displayName: column.displayName, isHidden: true, condition: null });
        } else {
          columns.push({ id: column.id, displayName: column.displayName, isHidden: false, condition: column.condition });
        }
      });
      this._tabs[this.tabId][this.workItemType].columnSettings = columns;
    } else {
      this._tabs[this.tabId][this.workItemType].columnSettings = JSON.parse(savedSetting);
    }

    this.calculateColumnWidthSum();
  }

  private _initTab(tabId: string, workItemType: string): void {
    this.gridSetting = this._tabs[tabId][workItemType];
    this.defaultColumnsWithTimeConditions = this._tabs[this.tabId][this.workItemType].defaultColumnsWithTimeConditions;
    this.selectedPreviewItem = null;
  }

  private _getSelectedDepartmentIDs(): string[] {
    if (this.selectedDepartments) {
      return this.selectedDepartments.map(department => department.id);
    }
  }

  private _getInactiveWorkItems(folderIds: string[]) {
    if (this.isListLoading) {
      return;
    }
    this.isListLoading = true;
    if (!this._startDate) {
      this._startDate = Moment().startOf('day').valueOf();
    }
    if (!this._endDate) {
      this._endDate = Moment().valueOf();
    }
    let operatorIds = null;
    if (this.isOwnFilterActive) {
      operatorIds = [this._userContextService.userContext.id];
    }
    this._detectChanges();
    this._bcRestApi[this.gridSetting.restApiEndpointName](this._getSelectedDepartmentIDs(), folderIds, this._startDate, this._endDate, operatorIds)
      .then((res) => {
        this.isListLoading = true;
        this._clearSubscriptions();
        this.gridItemList = res;
        this.gridItemList.forEach((item, index) => {
          this.gridItemList[index] = <GridItem>WorkItemAdapter.convertStaticQueryItemToWorkItem(item, this.workItemType);
          this.setWarningsForOneItem(this.gridItemList[index]);
        });
        this._listItemsUpdated();
        this.refreshTimestamp = Moment().format('HH:mm');
        this.isListLoading = false;
        this._detectChanges();
      }).catch(err => {
      this.isListLoading = false;
      this._logger.logError('Error occurred when retrieving inactive workItems: {}', err);
    });
  }

  private _clearSubscriptions(): void {
    if (this._insertSubscription) {
      this._insertSubscription.unsubscribe();
      this._insertSubscription = null;
    }
    if (this._updateSubscription) {
      this._updateSubscription.unsubscribe();
      this._updateSubscription = null;
    }
    if (this._deleteSubscription) {
      this._deleteSubscription.unsubscribe();
      this._deleteSubscription = null;
    }
    if (this._doneSubscription) {
      this._doneSubscription.unsubscribe();
      this._doneSubscription = null;
    }
  }

  private _clearWindowSizeSubscription(): void {
    if (this._windowSizeSubscription) {
      this._windowSizeSubscription.unsubscribe();
      this._windowSizeSubscription = null;
    }
  }

  private _clearFoldersSubscriptions(): void {
    if (this._foldersSubscription) {
      this._foldersSubscription.unsubscribe();
      this._foldersSubscription = null;
    }
  }

  private _initGridItems(folderIds: string[] | string): void {
    folderIds = this._formatFolders(folderIds);
    if (this.gridSetting.restApiEndpointName) {
      this._getInactiveWorkItems(folderIds);
    } else {
      this.initSubscriptions(folderIds);
    }
  }

  private _storeDatePickerSettings(): void {
    let dateToBeStored = { startDate: this._startDate, endDate: this._endDate };
    this.localStorageService.setItem(bcSessionKeys.storage.queueViewSelectedDate + '-' + this.workItemType.toLowerCase(), JSON.stringify(dateToBeStored));
  }

  private _setDatesByWorkItemType(workItemType: string): void {
    let activeDate: CalendarDate;
    let savedDateSetting = this._getDatePickerSettings(workItemType);
    if (!savedDateSetting) {
      activeDate = this.datePicker.dates[0];
    } else {
      let savedFixDate = this._findSavedDateInFixedDates(savedDateSetting);
      if (savedFixDate) {
        activeDate = savedFixDate;
      } else {
        activeDate = this.datePicker.dates[this.datePicker.dates.length - 1];
        activeDate.startDate = Moment(savedDateSetting.startDate);
        activeDate.endDate = Moment(savedDateSetting.endDate);
      }
    }
    this.datePicker.setInterval(activeDate);
  }

  private _getDatePickerSettings(workItemType: string): CalendarDate {
    const savedDatePickerSetting = this.localStorageService.getItem(bcSessionKeys.storage.queueViewSelectedDate + '-' + workItemType.toLowerCase());
    if (savedDatePickerSetting) {
      return JSON.parse(savedDatePickerSetting);
    }
    return null;
  }

  private _findSavedDateInFixedDates(savedDate): CalendarDate {
    return this.datePicker.dates.find((item) => {
      if (item.endDate && item.startDate) {
        let areStartDatesTheSame = item.startDate.valueOf() === savedDate.startDate;
        let areEndDatesTheSame = item.endDate.valueOf() === savedDate.endDate;
        return areStartDatesTheSame && areEndDatesTheSame;
      }
      return false;
    });
  }
}
